import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import RefreshIcon from "@material-ui/icons/Refresh";
import ShareIcon from "@material-ui/icons/Share";
import GetAppIcon from "@material-ui/icons/GetApp";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CloseIcon from "@material-ui/icons/Close";
import ZoomOutMapIcon from "@material-ui/icons/ZoomOutMap";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import GroupIcon from "@material-ui/icons/Group";
import PhoneAndroidIcon from "@material-ui/icons/PhoneAndroid";
import EditIcon from "@material-ui/icons/Edit";
import DescriptionIcon from "@material-ui/icons/Description";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import RepeatIcon from "@material-ui/icons/Repeat";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useDialogStyles } from "../../theme/redesign";
import { EmptyHint, FilterChips, LoadingHint, NegocioPager, PageFrame, SearchField, tNegocio, useMobile, usePager } from "./shared";
import { setPublicidadDraft } from "./publicidadDraft";
import PublicidadPlanes from "./PublicidadPlanes";

const t = (key) => tNegocio(`pages.publicidad.${key}`);
const TYPES = ["", "pelicula_dia", "serie_recomendada", "actualidad_streaming", "fin_de_semana_streaming", "promo_plataformas"];
const TONE = {
  pelicula_dia: "#1d4ed8",
  serie_recomendada: "#6d28d9",
  actualidad_streaming: "#0f766e",
  fin_de_semana_streaming: "#b45309",
  promo_plataformas: "#be123c",
};

const DESTINATIONS = [
  { id: "campaigns", path: "/campaigns", icon: AnnouncementIcon, titleKey: "dest.campaigns", hintKey: "dest.campaignsHint", hasType: true },
  { id: "groups", path: "/group-campaigns", icon: GroupIcon, titleKey: "dest.groups", hintKey: "dest.groupsHint", hasType: true },
  { id: "status", path: "/status-campaigns", icon: PhoneAndroidIcon, titleKey: "dest.status", hintKey: "dest.statusHint", hasType: true },
];

const CAMPAIGN_TYPES = [
  { id: "UNICA", icon: FlashOnIcon, titleKey: "kind.fast", hintKey: "kind.fastHint" },
  { id: "RECURRENTE", icon: RepeatIcon, titleKey: "kind.recurring", hintKey: "kind.recurringHint" },
];

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    margin: "2px 0 10px",
    flexWrap: "wrap",
  },
  count: {
    fontSize: 12,
    fontWeight: 700,
    color: theme.palette.text.secondary,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(132px, 1fr))",
    gap: 10,
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "repeat(auto-fill, minmax(148px, 1fr))",
    },
  },
  card: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  media: {
    position: "relative",
    cursor: "pointer",
    background: "rgba(127,127,127,0.08)",
  },
  image: {
    width: "100%",
    aspectRatio: "2 / 3",
    objectFit: "cover",
    display: "block",
  },
  badge: {
    position: "absolute",
    top: 6,
    left: 6,
    maxWidth: "calc(100% - 12px)",
    padding: "2px 6px",
    borderRadius: 999,
    color: "#fff",
    fontSize: 10,
    fontWeight: 800,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  when: {
    position: "absolute",
    right: 6,
    bottom: 6,
    padding: "2px 6px",
    borderRadius: 999,
    background: "rgba(15,23,42,0.72)",
    color: "#fff",
    fontSize: 10,
    fontWeight: 700,
  },
  body: {
    padding: 8,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 0,
  },
  title: {
    margin: 0,
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.25,
    cursor: "pointer",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    minHeight: 30,
  },
  icons: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "auto",
  },
  studio: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 220px) minmax(0, 1fr)",
    gap: 14,
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
  },
  posterWrap: { position: "relative" },
  poster: {
    width: "100%",
    borderRadius: 12,
    display: "block",
    cursor: "zoom-in",
    background: "#0f172a",
  },
  zoom: {
    position: "absolute",
    right: 8,
    bottom: 8,
    background: "rgba(15,23,42,0.72)",
    color: "#fff",
  },
  editor: { minWidth: 0, display: "flex", flexDirection: "column", gap: 10 },
  destGrid: {
    display: "grid",
    gap: 8,
  },
  dest: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    textAlign: "left",
    width: "100%",
    padding: "10px 12px",
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    cursor: "pointer",
  },
  destIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "grid",
    placeItems: "center",
    background: "rgba(37,99,235,0.1)",
    color: theme.palette.primary.main,
    flex: "0 0 auto",
  },
  destTitle: { fontWeight: 800, fontSize: 14 },
  destHint: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 2 },
  meta: { fontSize: 12, fontWeight: 700, color: theme.palette.text.secondary },
  actions: { display: "flex", flexWrap: "wrap", gap: 4 },
  head: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 },
  close: { marginTop: -6 },
  kicker: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    color: theme.palette.primary.main,
  },
  studioTitle: { margin: "2px 0 0", fontSize: 18, fontWeight: 800, lineHeight: 1.2 },
  intro: {
    margin: "0 0 14px",
    padding: "14px 16px",
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)",
  },
  introTitle: { margin: 0, fontSize: 16, fontWeight: 800, lineHeight: 1.3 },
  introBody: { margin: "8px 0 0", fontSize: 13, lineHeight: 1.5, color: theme.palette.text.secondary },
  introWhy: { margin: "8px 0 0", fontSize: 13, lineHeight: 1.5, fontWeight: 600 },
  introActions: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 },
}));

const LANDING_URL = "https://iapublicidad.sdpro.shop/";

const relativeTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const minutes = Math.round((date.getTime() - Date.now()) / 60000);
  const format = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (Math.abs(minutes) < 60) return format.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 36) return format.format(hours, "hour");
  return format.format(Math.round(hours / 24), "day");
};

const copyText = async (value) => {
  await navigator.clipboard.writeText(value || "");
  toast.success(t("copied"));
};

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

const imageFile = async (id) => {
  const { data } = await api.get(`/negocio/publicidad/feed/${encodeURIComponent(id)}/imagen`, { responseType: "blob" });
  const type = String(data.type || "image/jpeg").split(";")[0];
  if (!type.startsWith("image/")) throw new Error("image");
  const ext = type.includes("png") ? "png" : "jpg";
  return new File([data], `publicacion.${ext}`, { type });
};

const sharePiece = async (item, text) => {
  try {
    const file = item.image_url ? await imageFile(item.id) : null;
    const payload = { title: item.titulo || t("title"), text: text || "" };
    if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ ...payload, files: [file] });
      toast.success(t("shared"));
      return;
    }
    if (navigator.share) {
      await navigator.share(payload);
      toast.success(t("shared"));
      return;
    }
    await navigator.clipboard.writeText(text || "");
    if (file) saveBlob(file, file.name);
    toast.success(t("shareReady"));
  } catch (error) {
    if (error?.name === "AbortError") return;
    throw error;
  }
};

const downloadImage = async (item) => {
  const file = await imageFile(item.id);
  saveBlob(file, file.name);
  toast.success(t("downloaded"));
};

const downloadTextFile = (text) => {
  saveBlob(new Blob([text || ""], { type: "text/plain;charset=utf-8" }), "publicacion.txt");
  toast.success(t("downloaded"));
};

const typeLabel = (tipo) => (tipo ? t(`types.${tipo}`) : "");

function IconAction({ title, onClick, disabled, children, color }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton size="small" color={color || "default"} onClick={onClick} disabled={disabled} aria-label={title}>
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function PieceCard({ item, onOpen, onShare, onDownloadImage }) {
  const classes = useStyles();
  return (
    <article className={classes.card}>
      <div className={classes.media} onClick={() => onOpen(item)} role="presentation">
        {item.image_url ? (
          <img className={classes.image} src={item.image_url} alt={item.titulo || ""} loading="lazy" />
        ) : <div className={classes.image} />}
        {item.tipo ? <span className={classes.badge} style={{ background: TONE[item.tipo] || "#334155" }}>{typeLabel(item.tipo)}</span> : null}
        {relativeTime(item.created_at) ? <span className={classes.when}>{relativeTime(item.created_at)}</span> : null}
      </div>
      <div className={classes.body}>
        <h3 className={classes.title} onClick={() => onOpen(item)} title={item.titulo}>{item.titulo}</h3>
        <div className={classes.icons}>
          <IconAction title={t("open")} onClick={() => onOpen(item)} color="primary"><EditIcon fontSize="small" /></IconAction>
          <IconAction title={t("downloadImage")} onClick={() => onDownloadImage(item)} disabled={!item.image_url}><GetAppIcon fontSize="small" /></IconAction>
          <IconAction title={t("downloadText")} onClick={() => downloadTextFile(item.texto)}><DescriptionIcon fontSize="small" /></IconAction>
          <IconAction title={t("share")} onClick={() => onShare(item)}><ShareIcon fontSize="small" /></IconAction>
        </div>
      </div>
    </article>
  );
}

function Studio({ item, onClose }) {
  const classes = useStyles();
  const dlg = useDialogStyles();
  const mobile = useMobile();
  const history = useHistory();
  const [text, setText] = useState(item.texto || "");
  const [zoom, setZoom] = useState(false);
  const [busy, setBusy] = useState(false);
  const [destination, setDestination] = useState(null);

  const run = async (work) => {
    setBusy(true);
    try { await work(); } catch (error) { toastError(error); } finally { setBusy(false); }
  };

  const sendTo = async (nextDestination, campaignType = "UNICA") => {
    setBusy(true);
    try {
      const file = item.image_url ? await imageFile(item.id) : null;
      setPublicidadDraft({
        title: item.titulo || t("title"),
        text: text || "",
        file,
        sourceId: item.id,
        destination: nextDestination.id,
        campaignType: campaignType === "RECURRENTE" ? "RECURRENTE" : "UNICA",
      });
      toast.success(t("opening"));
      onClose();
      history.push(nextDestination.path);
    } catch (error) {
      toastError(error);
    } finally {
      setBusy(false);
    }
  };

  const pickDestination = (next) => {
    if (next.hasType) {
      setDestination(next);
      return;
    }
    sendTo(next, "UNICA");
  };

  return (
    <>
      <Dialog open onClose={onClose} fullScreen={mobile} fullWidth maxWidth="md" PaperProps={{ className: dlg.paper }}>
        <DialogContent>
          <div className={classes.head}>
            <div>
              <div className={classes.kicker}>{typeLabel(item.tipo) || t("title")}</div>
              <h2 className={classes.studioTitle}>{item.titulo}</h2>
            </div>
            <IconButton className={classes.close} onClick={onClose} aria-label={t("close")}><CloseIcon /></IconButton>
          </div>
          <div className={classes.studio}>
            <div className={classes.posterWrap}>
              {item.image_url ? (
                <img className={classes.poster} src={item.image_url} alt={item.titulo || ""} onClick={() => setZoom(true)} />
              ) : null}
              {item.image_url ? (
                <Tooltip title={t("zoom")}>
                  <IconButton className={classes.zoom} onClick={() => setZoom(true)} aria-label={t("zoom")}><ZoomOutMapIcon /></IconButton>
                </Tooltip>
              ) : null}
            </div>
            <div className={classes.editor}>
              <TextField
                fullWidth
                multiline
                rows={mobile ? 5 : 7}
                variant="outlined"
                label={t("preview")}
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
              <div className={classes.meta}>{t("chars").replace("{n}", String(text.length))}</div>
              <div className={classes.actions}>
                <IconAction title={t("downloadImage")} disabled={busy || !item.image_url} onClick={() => run(() => downloadImage(item))} color="primary"><GetAppIcon /></IconAction>
                <IconAction title={t("downloadText")} disabled={busy} onClick={() => downloadTextFile(text)} color="primary"><DescriptionIcon /></IconAction>
                <IconAction title={t("copy")} disabled={busy} onClick={() => run(() => copyText(text))}><FileCopyIcon /></IconAction>
                <IconAction title={t("share")} disabled={busy} onClick={() => run(() => sharePiece(item, text))}><ShareIcon /></IconAction>
                <Button size="small" disabled={busy || text === (item.texto || "")} onClick={() => setText(item.texto || "")}>{t("reset")}</Button>
              </div>
              <DialogTitle disableTypography style={{ padding: "8px 0 0", fontSize: 15, fontWeight: 800 }}>
                {destination ? t("kind.title") : t("useIn")}
              </DialogTitle>
              {destination ? (
                <div className={classes.destGrid}>
                  <button type="button" className={classes.dest} disabled={busy} onClick={() => setDestination(null)}>
                    <span className={classes.destIcon}><ArrowBackIcon /></span>
                    <span>
                      <div className={classes.destTitle}>{t(destination.titleKey)}</div>
                      <div className={classes.destHint}>{t("kind.back")}</div>
                    </span>
                  </button>
                  {CAMPAIGN_TYPES.map((kind) => {
                    const Icon = kind.icon;
                    return (
                      <button
                        key={kind.id}
                        type="button"
                        className={classes.dest}
                        disabled={busy}
                        onClick={() => sendTo(destination, kind.id)}
                      >
                        <span className={classes.destIcon}>{busy ? <CircularProgress size={18} /> : <Icon />}</span>
                        <span>
                          <div className={classes.destTitle}>{t(kind.titleKey)}</div>
                          <div className={classes.destHint}>{t(kind.hintKey)}</div>
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className={classes.destGrid}>
                  {DESTINATIONS.map((option) => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        className={classes.dest}
                        disabled={busy}
                        onClick={() => pickDestination(option)}
                      >
                        <span className={classes.destIcon}><Icon /></span>
                        <span>
                          <div className={classes.destTitle}>{t(option.titleKey)}</div>
                          <div className={classes.destHint}>{t(option.hintKey)}</div>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={zoom} onClose={() => setZoom(false)} maxWidth="sm" fullWidth>
        <img src={item.image_url || ""} alt={item.titulo || ""} style={{ width: "100%", display: "block" }} />
      </Dialog>
    </>
  );
}

export default function PublicidadPage() {
  const classes = useStyles();
  const [section, setSection] = useState("material");
  const [tipo, setTipo] = useState("");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [studio, setStudio] = useState(null);

  useEffect(() => {
    if (section !== "material") return undefined;
    let alive = true;
    setLoading(true);
    api.get("/negocio/publicidad/feed", { params: { limit: 50 } })
      .then(({ data }) => { if (alive) setItems(Array.isArray(data?.items) ? data.items : []); })
      .catch((error) => { if (alive) { toastError(error); setItems([]); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tick, section]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items.filter((item) => {
      if (tipo && item.tipo !== tipo) return false;
      if (!needle) return true;
      const haystack = [item.titulo, item.texto, ...(item.productos || []).map((product) => `${product.nombre} ${product.precio}`)].join("\n").toLowerCase();
      return haystack.includes(needle);
    });
  }, [items, tipo, query]);

  const pager = usePager(visible, 12);
  const share = (item) => sharePiece(item, item.texto).catch(toastError);
  const saveImage = (item) => downloadImage(item).catch(toastError);

  return (
    <PageFrame
      title={t("title")}
      subtitle={t("subtitle")}
      actions={
        section === "material" ? (
          <Tooltip title={t("refresh")}>
            <span>
              <IconButton onClick={() => setTick((value) => value + 1)} disabled={loading} aria-label={t("refresh")}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : null
      }
    >
      <div className={classes.intro}>
        <h2 className={classes.introTitle}>{t("introTitle")}</h2>
        <p className={classes.introBody}>{t("introBody")}</p>
        <p className={classes.introWhy}>{t("introWhy")}</p>
        <div className={classes.introActions}>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            href={LANDING_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("introCta")}
          </Button>
          {section !== "planes" ? (
            <Button size="small" variant="contained" color="primary" onClick={() => setSection("planes")}>
              {t("introPlans")}
            </Button>
          ) : null}
        </div>
      </div>
      <FilterChips
        value={section}
        onChange={setSection}
        options={[
          { value: "material", label: t("sections.material") },
          { value: "planes", label: t("sections.plans") },
        ]}
      />
      {section === "planes" ? <PublicidadPlanes /> : null}
      {section === "material" ? (
        <>
          <FilterChips value={tipo} onChange={setTipo} options={TYPES.map((value) => ({ value, label: t(value ? `types.${value}` : "types.all") }))} />
          <div className={classes.toolbar}>
            <SearchField value={query} onChange={setQuery} placeholder={t("search")} />
            <div className={classes.count}>{t("pieces").replace("{n}", String(visible.length))}</div>
          </div>
          {loading && !items.length ? <LoadingHint /> : null}
          {!loading && !visible.length ? <EmptyHint title={query.trim() ? t("emptySearch") : t("empty")} subtitle={query.trim() ? "" : t("emptyHint")} /> : null}
          <div className={classes.grid}>
            {pager.slice.map((item) => (
              <PieceCard key={item.id} item={item} onOpen={setStudio} onShare={share} onDownloadImage={saveImage} />
            ))}
          </div>
          <NegocioPager
            count={pager.count}
            page={pager.page}
            rowsPerPage={pager.rowsPerPage}
            onChangePage={pager.setPage}
            onChangeRowsPerPage={pager.setRowsPerPage}
            hideBelow={12}
          />
          {studio ? <Studio key={studio.id} item={studio} onClose={() => setStudio(null)} /> : null}
        </>
      ) : null}
    </PageFrame>
  );
}
